import { IBasketItem } from '@/context/BasketStore'
import { IOption, IProduct } from '@/context/ProductDataStore'
import { DiscountCode } from '@/context/DiscountStore'

export type FreeAddonCandidate = {
  optionId: string
  optionTitle: string
  addon: IProduct
}

const isDiscountFreeAddonActive = (discountCode: DiscountCode | null): boolean => {
  return Boolean(discountCode && discountCode.valid && discountCode.discountType === 'free_addon')
}

const canUseAddonForSelection = (addon: IProduct): boolean => {
  return addon.stockLimits?.availableForSale !== false
}

const getMatchingAddonFromOption = (
  option: IOption,
  freeAddonIds: Set<string>
): IProduct | null => {
  const preferredById = option.options.find((product) => (
    freeAddonIds.has(String(product._id)) && canUseAddonForSelection(product)
  ))
  if (preferredById) {
    return preferredById
  }

  const firstAvailable = option.options.find((product) => canUseAddonForSelection(product))
  return firstAvailable || null
}

export const getEligibleFreeAddonCandidate = (
  item: IBasketItem,
  discountCode: DiscountCode | null
): FreeAddonCandidate | null => {
  if (!isDiscountFreeAddonActive(discountCode)) {
    return null
  }

  const freeAddonTypes = new Set((discountCode?.freeAddonTypes || []).map(String))
  const freeAddonIds = new Set((discountCode?.freeAddonIds || []).map(String))
  if (!freeAddonTypes.size && !freeAddonIds.size) {
    return null
  }

  const alreadyHasFreeAddon = item.extras.some((addon) => (
    freeAddonTypes.has(String(addon.productCatalogueId)) || freeAddonIds.has(String(addon._id))
  ))
  if (alreadyHasFreeAddon) {
    return null
  }

  const availableOptions = item.colourWay.availableOptions || []
  for (const option of availableOptions) {
    if (option.required) {
      continue
    }

    const optionMatchesType = freeAddonTypes.has(String(option.productCatalogueId))
    const optionMatchesIds = option.options.some((product) => freeAddonIds.has(String(product._id)))
    if (!optionMatchesType && !optionMatchesIds) {
      continue
    }

    const addon = getMatchingAddonFromOption(option, freeAddonIds)
    if (!addon) {
      continue
    }

    return {
      optionId: option.productCatalogueId,
      optionTitle: option.title || 'addon',
      addon
    }
  }

  return null
}

export const addEligibleFreeAddonToBasketItem = (
  item: IBasketItem,
  discountCode: DiscountCode | null,
  engravingText?: string
): Partial<IBasketItem> | null => {
  const candidate = getEligibleFreeAddonCandidate(item, discountCode)
  if (!candidate) {
    return null
  }

  const nextExtras = [...item.extras, candidate.addon]
  let nextExtrasData = [...item.extrasData]

  if (candidate.addon.componentModifications?.engravingText) {
    const maxLength = candidate.addon.componentModifications.engravingText.maxLength || 14
    const normalizedEngravingText = (engravingText || '')
      .replace(/[^A-Za-z0-9\s.,!?\-'/]/g, '')
      .slice(0, maxLength)
    const hasInputData = item.extrasData.some((data) => data.itemId === candidate.addon._id)
    if (!hasInputData) {
      nextExtrasData = [
        ...nextExtrasData,
        {
          optionId: candidate.optionId,
          itemId: candidate.addon._id,
          inputValue: normalizedEngravingText
        }
      ]
    } else {
      nextExtrasData = nextExtrasData.map((data) => (
        data.itemId === candidate.addon._id
          ? { ...data, inputValue: normalizedEngravingText }
          : data
      ))
    }
  }

  return {
    extras: nextExtras,
    extrasData: nextExtrasData
  }
}
